import { BigNumber } from "@ethersproject/bignumber"

import { bigNumberToNumber } from "common/helpers/bignumber"
import { getTimestampFromIetf } from "common/helpers/date"
import type { OnchainVote, Vote } from "query/graphql"

export type TimelineDot = {
  voter: string
  amount: number
  type: string
  timestamp: number
  supportTotals: {
    [type: string]: BigNumber
  }
}

export const generateTimelineChartData = (
  votes: Vote[],
  decimals: number,
  createdAt: string,
  choices?: string[] | null,
): TimelineDot[] => {
  const sortedVotes = votes
    .filter((vote): vote is OnchainVote => "block" in vote)
    .sort((a, b) => {
      if ("block" in a && "block" in b) {
        return Date.parse(a.block.timestamp) - Date.parse(b.block.timestamp)
      }

      return 0
    })

  const firstDot: TimelineDot = {
    voter: "",
    amount: 0,
    type: "",
    timestamp: getTimestampFromIetf(createdAt),
    supportTotals: (choices || []).reduce(
      (prev, choice) => ({
        ...prev,
        [choice]: BigNumber.from(0),
      }),
      {} as { [type: string]: BigNumber },
    ),
  }

  const dots: TimelineDot[] = sortedVotes.map((vote, index, votes) => {
    const { type, voter, amount } = vote
    const { address } = voter

    const timestamp = getTimestampFromIetf(vote.block.timestamp)

    const supportTotals = votes.reduce((prev, vote, i) => {
      if (i <= index) {
        const { type, amount } = vote

        if (prev[type] !== undefined) {
          return {
            ...prev,
            [type]: BigNumber.from(amount).add(prev[type]),
          }
        }

        return {
          ...prev,
          [type]: BigNumber.from(amount),
        }
      }

      return prev
    }, {} as { [support: string]: BigNumber })

    return {
      voter: address,
      amount: bigNumberToNumber(BigNumber.from(amount), decimals),
      type,
      timestamp,
      supportTotals,
    }
  })

  return [firstDot, ...dots]
}
